import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { api} from '../../../utils/variables';
import { userProfileApi } from '../../../services/userProfileApi';
import useSWR, { mutate as swrMutate } from 'swr';
import AppsTrackingPlanModal from '../../Common/Modal/AppsTrackingPlanModal';
import { usePlatforms } from '../../../contexts/PlatformsContext';
// Use the API base URL from environment
const API_BASE_URL = api;


// Create context
const CategoryAnalysisContext = createContext();

// Custom hook to use the context
export const useCategoryAnalysis = () => {
  const context = useContext(CategoryAnalysisContext);
  if (!context) {
    throw new Error('useCategoryAnalysis must be used within a CategoryAnalysisProvider');
  }
  return context;
};

// Provider component
export const CategoryAnalysisProvider = ({ children }) => {
  const { getActivePlatforms } = usePlatforms();

  // Create axios instance
  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });


  // State for tabs — default to first active platform (same as Keyword Explorer)
  const [activeTab, setActiveTab] = useState(() => {
    const activePlatforms = getActivePlatforms();
    return activePlatforms.length > 0 ? activePlatforms[0].id : 'meta';
  });

  // Platform sent to the API follows the active tab
  const platform = activeTab || '';

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    // Client-side filtering only; SWR refetches when the key's platform param changes
  }, []);

  // State for date range
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    // Format dates as YYYY-MM-DD for input type="date"
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(oneYearAgo),
      to: formatDate(today)
    };
  });

  // State for stats
  const [stats, setStats] = useState({
    // Default to N/A so UI doesn't show static data before API response
    downloads: { value: 'N/A', change: 'N/A' },
    revenue: { value: '$0', change: 'N/A' },
    games: { value: 'N/A', change: 'N/A' },
    reviews: { value: 'N/A', change: 'N/A' }
  });

  // Formatting helpers
  const formatNumber = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return 'N/A';
    try {
      return new Intl.NumberFormat('en-US').format(Number(n));
    } catch (e) {
      return String(n);
    }
  };

  const formatCurrency = (n, currency = 'USD') => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return 'N/A';
    try {
      // Round to nearest integer for totals (no cents). Use Intl for thousands separators.
      const rounded = Math.round(Number(n));
      return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(rounded);
    } catch (e) {
      return String(n);
    }
  };

  const formatPercentChange = (current, previous) => {
    if (previous === null || previous === undefined || Number(previous) === 0) return 'N/A';
    const cur = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (prev === 0) return 'N/A';
    const change = ((cur - prev) / Math.abs(prev)) * 100;
    const rounded = Math.round(change * 10) / 10; // one decimal
    return `${change >= 0 ? '+' : ''}${rounded}%`;
  };

  // State for keywords
  const [activeKeyword, setActiveKeyword] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(true);
  const [keywordsError, setKeywordsError] = useState(null);

  // Fetch keywords from API
  const fetchKeywords = async () => {
    try {
      setKeywordsLoading(true);
      setKeywordsError(null);

      // Call the new trending keywords endpoint which returns { message, data: [...], details, key }
      const response = await axiosInstance.get(`/tracker/dashboard/trending_categories`);


      // Normalize the new response shape where keywords live in `data` array
      const kws = Array.isArray(response?.data?.data) ? response.data.data : (Array.isArray(response?.data?.keywords) ? response.data.keywords : []);
      if (kws.length) {
        setKeywords(kws);
      } else {
        // If no array found, throw to be handled by catch
        throw new Error('Invalid data format received from keywords API');
      }
    } catch (error) {
      console.error('Error fetching keywords:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setKeywordsError(error.response?.data?.message || error.message || 'Failed to fetch keywords');
    } finally {
      setKeywordsLoading(false);
    }
  };

  // Fetch keywords on component mount
  useEffect(() => {
    fetchKeywords();
  }, []);

  // State for table data
  const [tableData, setTableData] = useState([]);
  const [tableDataLoading, setTableDataLoading] = useState(true);
  const [tableDataError, setTableDataError] = useState(null);
  // Timestamp for when table data was last updated
  const [lastUpdated, setLastUpdated] = useState(null);
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Detect if user is Explorer (Tier-1) -> no product attached
  // Start in loading state so UI can show skeleton until we know the plan.
  const [isExplorerUser, setIsExplorerUser] = useState(null);
  const [isPlanLoading, setIsPlanLoading] = useState(true);

  // On mount, fetch current plan and determine Explorer status. Default to Explorer on error (safe lock)
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsPlanLoading(true);
      try {
        const res = await userProfileApi.fetchCurrentPlan();
        // API returns { data: { product: ... } }
        const product = res && res.data && res.data.product ? res.data.product : null;

        // If subscription shows cancelled/ended, treat as Explorer (locked)
        const subscriptionStatus = (res && res.data && (res.data.status || res.data.subscription_status || '')).toString().toLowerCase();
        if (subscriptionStatus.includes('cancel') || subscriptionStatus.includes('ended') || subscriptionStatus.includes('inactive')) {
          if (mounted) {
            setIsExplorerUser(true);
            setIsPlanLoading(false);
          }
          return;
        }

        // Decide whether this product grants access to Category Analysis.
        // We don't have a definitive mapping in the frontend, so match by product name keywords.
        const name = (product && (product.name || product.title || product.id)) ? String(product.name || product.title || product.id).toLowerCase() : '';

        // Keywords indicating paid plans that should unlock the feature
        const allowedKeywords = ['pro', 'premium', 'growth', 'business', 'enterprise', 'team', 'standard'];
        // Keywords indicating plans that are free/limited and should remain locked
        const deniedKeywords = ['basic', 'starter', 'explorer', 'free', 'trial', 'lite'];

        const hasAllowed = allowedKeywords.some(k => name.includes(k));
        const hasDenied = deniedKeywords.some(k => name.includes(k));

        // If product explicitly matches allowed -> unlock. If matches denied -> lock.
        // If ambiguous (no keyword match) default to locked (safe).
        const unlock = hasAllowed && !hasDenied;
        if (mounted) {
          setIsExplorerUser(!unlock);
          setIsPlanLoading(false);
        }
      } catch (e) {
        console.error('Error fetching current plan for Category Analysis:', e);
        // On error assume explorer (lock features) - safer default
        if (mounted) {
          setIsExplorerUser(true);
          setIsPlanLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Helper to detect upgrade responses/errors from API
  const isUpgradeResponse = (data) => {
    if (!data || typeof data !== 'object') return false;
    const status = data?.status;
    const action = (data?.action || '').toString().toUpperCase();
    return status === 400 && action === 'UPGRADE';
  };
  const isUpgradeError = (error) => {
    const response = error?.response;
    const data = response?.data || {};
    return isUpgradeResponse(data);
  };
  // Filters applied to category searches (keys: price, rating, genre, bundle, number_of_players, sort_by, etc.)
  const [categoryFilters, setCategoryFilters] = useState({});

  // SWR fetcher using axiosInstance
  const swrFetcher = async (url) => {
    const resp = await axiosInstance.get(url);
    return resp.data;
  };

  // Fetch table data from API (legacy one-off fetch; retained for manual calls)
  const fetchTableData = async (category) => {
    try {
      // Don't fetch if no category is provided
      if (!category) {
        setTableData([]);
        setTableDataLoading(false);
        return;
      }

      setTableDataLoading(true);
      setTableDataError(null);

      // Check if auth token exists
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authorization token found. Please login again.');
      }

      const response = await axiosInstance.get(`/tracker/category/${category}`);

      // If backend tells us to upgrade, surface the upgrade modal and stop
      if (isUpgradeResponse(response.data)) {
        setShowUpgradeModal(true);
        setTableData([]);
        setTableDataLoading(false);
        return;
      }


      if (response.data.status === 200 && response.data.games) {
        // Handle both nested and flat structures
        let gamesArray = [];
        if (response.data.games.games && Array.isArray(response.data.games.games)) {
          // Nested structure: response.data.games.games
          gamesArray = response.data.games.games;
        } else if (Array.isArray(response.data.games)) {
          // Flat structure: response.data.games
          gamesArray = response.data.games;
        } else {
          throw new Error('Invalid games data structure received from API');
        }
        setTableData(gamesArray);
        setLastUpdated(new Date());
      } else {
        throw new Error('Invalid data format received from API');
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      // If server indicates upgrade, open modal
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
      }
      setTableDataError(error.response?.data?.message || error.message || 'Failed to fetch data');
      // Set empty array on error
      setTableData([]);
    } finally {
      setTableDataLoading(false);
    }
  };

  // Fetch table data when active keyword changes
  useEffect(() => {
    if (activeKeyword) {
      // When a search/keyword is active we'll rely on SWR polling below.
      // Set loading true so UI shows skeleton.
      setTableDataLoading(true);
      setTableDataError(null);
    } else {
      // Clear table data when no keyword is selected
      setTableData([]);
      setTableDataLoading(false);
      setTableDataError(null);
      // Reset stats cards so stale values don't linger after clearing search
      setStats({
        downloads: { value: 0, change: 'N/A' },
        revenue: { value: '$0', change: 'N/A' },
        games: { value: '0', change: 'N/A' },
        reviews: { value: '0', change: 'N/A' }
      });
    }
  }, [activeKeyword]);

  // Helper to serialize filters into query params. Arrays join with ', ' to match backend expectation.
  const serializeFilters = (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || k === 'platform') return;
      if (Array.isArray(v)) {
        params.append(k, v.join(', '));
      } else {
        params.append(k, String(v));
      }
    });
    return params.toString();
  };

  // Filter table data by platform (same pattern as Keyword Explorer filteredSearchResults)
  const filteredTableData = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    const filterPlatform = categoryFilters?.platform
      ? (Array.isArray(categoryFilters.platform) ? categoryFilters.platform[0] : categoryFilters.platform)
      : null;
    const effectivePlatform = filterPlatform || platform || activeTab;

    if (platform && !filterPlatform) return tableData;

    return tableData.filter((row) => {
      const rowPlatform = row?.platform || row?.game?.platform || '';
      return String(rowPlatform).toLowerCase() === String(effectivePlatform).toLowerCase();
    });
  }, [tableData, activeTab, platform, categoryFilters]);

  const applyCategoryFilters = useCallback((filters) => {
    setCategoryFilters(filters || {});

    if (filters?.platform) {
      const filterPlatform = Array.isArray(filters.platform) ? filters.platform[0] : filters.platform;
      if (filterPlatform) setActiveTab(filterPlatform);
    }

    if (activeKeyword && String(activeKeyword).trim()) {
      const term = String(activeKeyword).trim();
      setActiveKeyword('');
      setTimeout(() => setActiveKeyword(term), 50);
    }
  }, [activeKeyword]);

  // Use SWR to poll the category endpoint while an activeKeyword exists
  // Include platform and serialized filters in the key so different filter combinations are cached separately.
  const filtersQuery = serializeFilters(categoryFilters);
  const swrKey = activeKeyword
    ? `/tracker/category/${encodeURIComponent(activeKeyword)}?platform=${encodeURIComponent(platform)}${filtersQuery ? `&${filtersQuery}` : ''}`
    : null;
  const { data: swrData, error: swrError } = useSWR(swrKey, swrFetcher, {
    // poll every 5s while API indicates processing/LOADING; otherwise stop
    refreshInterval: (latestData) => {
      if (!latestData) return 0;
      const action = (latestData.action || '').toString().toUpperCase();
      return action === 'LOADING' || action === 'IN_PROCESS' ? 5000 : 0;
    },
    revalidateOnFocus: false,
    shouldRetryOnError: true,
    dedupingInterval: 1000,
  });

  // On mount, restore last saved response so UI can show previous results instantly
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('categoryAnalysisLastResponse')
      if (!raw) return
      const saved = JSON.parse(raw)
      if (!saved || !saved.keyword) return

      // Only restore if there's no activeKeyword to avoid clobbering user actions
      if (!activeKeyword) {
        const { keyword: kw, platform: savedPlatform, filters, responseData, savedAt } = saved

        // Set filters, platform tab, and keyword so SWR will build the same key
        setCategoryFilters(filters || {})
        const filterPlatform = filters?.platform
          ? (Array.isArray(filters.platform) ? filters.platform[0] : filters.platform)
          : null
        if (savedPlatform || filterPlatform) {
          setActiveTab(savedPlatform || filterPlatform)
        }
        setActiveKeyword(kw)

        // Set table data directly so UI renders immediately
        try {
          if (responseData && (responseData.games || responseData.games?.games)) {
            let gamesArray = []
            if (responseData.games && responseData.games.games && Array.isArray(responseData.games.games)) {
              gamesArray = responseData.games.games
            } else if (Array.isArray(responseData.games)) {
              gamesArray = responseData.games
            }
            setTableData(gamesArray)
            setLastUpdated(savedAt ? new Date(savedAt) : new Date())
            setTableDataLoading(false)
          }
        } catch (e) {
          // ignore parse errors
        }

        // Seed SWR cache for the computed key so subsequent SWR hooks see the saved response
        try {
          const platformParamSaved = savedPlatform || platform || activeTab
          const paramsStr = serializeFilters(filters || {})
          const key = `/tracker/category/${encodeURIComponent(kw)}?platform=${encodeURIComponent(platformParamSaved)}${paramsStr ? `&${paramsStr}` : ''}`
          // Use swrMutate to set cache without revalidation
          swrMutate(key, responseData, false)
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore malformed storage
    }
  }, [])

  // React to SWR updates. While activeKeyword is set we show skeleton (tableDataLoading=true)
  useEffect(() => {
    if (!activeKeyword) return; // only handle when searching

    if (swrError) {
      console.error('SWR search error:', swrError);
      setTableDataError(swrError.response?.data?.message || swrError.message || 'Failed to fetch data');
      setTableDataLoading(false);
      setTableData([]);
      return;
    }

    if (!swrData) {
      // still polling, keep skeleton
      return;
    }

    // If backend indicates user must upgrade, open modal and stop processing
    if (isUpgradeResponse(swrData)) {
      setShowUpgradeModal(true);
      setTableData([]);
      setTableDataLoading(false);
      return;
    }

    // If backend indicates it's still processing, keep showing skeleton
    const action = (swrData.action || '').toString().toUpperCase();
    if (action !== 'DISPLAY' && action !== 'ERROR' && action !== 'FAILED') {
      // keep loading until DISPLAY or a terminal error state
      return;
    }

    // action is DISPLAY — set table data and stats
    try {
      const resp = swrData;
      if (resp.status === 200 && resp.games) {
        let gamesArray = [];
        if (resp.games.games && Array.isArray(resp.games.games)) {
          gamesArray = resp.games.games;
        } else if (Array.isArray(resp.games)) {
          gamesArray = resp.games;
        }
        setTableData(gamesArray);

        // set summary stats (reuse previous logic)
        const totals = resp.games;
        const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%";

        // raw totals (may be placeholder strings)
        const rawTotalDownloads = totals && (totals.total_downloads ?? null);
        const rawTotalMoney = totals && (totals.total_money ?? null);

        // If backend returned placeholder for totals OR any game has placeholder, keep placeholder so UI can detect locked state
        const anyGameHasDownloadPlaceholder = gamesArray.some(g => g && (g.downloads === LOCK_PLACEHOLDER || (typeof g.downloads === 'string' && g.downloads.includes(LOCK_PLACEHOLDER))));
        const anyGameHasRevenuePlaceholder = gamesArray.some(g => g && (g.revenue === LOCK_PLACEHOLDER || (typeof g.revenue === 'string' && g.revenue.includes(LOCK_PLACEHOLDER))));

        const downloadsValue = (rawTotalDownloads === LOCK_PLACEHOLDER || anyGameHasDownloadPlaceholder)
          ? LOCK_PLACEHOLDER
          : (rawTotalDownloads != null ? rawTotalDownloads : gamesArray.reduce((s, g) => s + (Number(g.downloads) || 0), 0));

        const revenueValue = (rawTotalMoney === LOCK_PLACEHOLDER || anyGameHasRevenuePlaceholder)
          ? LOCK_PLACEHOLDER
          : (rawTotalMoney != null ? rawTotalMoney : gamesArray.reduce((s, g) => s + (Number(g.revenue) || 0), 0));

        const totalGames = totals && (totals.total_games ?? gamesArray.length);
        const totalReviews = totals && (totals.total_reviews ?? gamesArray.reduce((s, g) => s + (Number(g.rating?.total_rating) || 0), 0));

        setStats({
          downloads: { value: (downloadsValue === LOCK_PLACEHOLDER ? LOCK_PLACEHOLDER : formatNumber(downloadsValue)), change: 'N/A' },
          revenue: { value: (revenueValue === LOCK_PLACEHOLDER ? LOCK_PLACEHOLDER : formatCurrency(revenueValue, 'USD')), change: 'N/A' },
          games: { value: formatNumber(totalGames), change: 'N/A' },
          reviews: { value: formatNumber(totalReviews), change: 'N/A' }
        });

        setLastUpdated(new Date());
        setTableDataLoading(false);
        setTableDataError(null);
      } else {
        // No games — set empty and stop loading
        setTableData([]);
        setTableDataLoading(false);
        setTableDataError(null);
      }
    } catch (e) {
      console.error('Error processing SWR data:', e);
      // keep showing skeleton to align with UX requirement
      setTableDataLoading(true);
    }
  }, [swrData, swrError, activeKeyword]);

  // Persist latest DISPLAY response so it can be restored when user returns
  useEffect(() => {
    try {
      if (activeKeyword && swrData) {
        const action = (swrData.action || '').toString().toUpperCase()
        if (action === 'DISPLAY') {
          const payload = {
            keyword: activeKeyword,
            platform: platform || '',
            filters: categoryFilters || {},
            responseData: swrData,
            savedAt: Date.now()
          }
          try { sessionStorage.setItem('categoryAnalysisLastResponse', JSON.stringify(payload)) } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      // ignore
    }
  }, [swrData, activeKeyword, JSON.stringify(categoryFilters || {})])


  // Close upgrade modal handler already provided by state setter

  // Value object to be provided to consumers
  const value = {
    activeTab,
    setActiveTab: handleTabChange,
    platform,
    dateRange,
    setDateRange,
    stats,
    setStats,
    activeKeyword,
    setActiveKeyword,
    keywords,
    setKeywords,
    keywordsLoading,
    keywordsError,
    fetchKeywords,
    tableData: filteredTableData,
    filteredTableData,
    setTableData,
    tableDataLoading,
    setTableDataLoading,
    tableDataError,
    lastUpdated,
    isExplorerUser,
    isPlanLoading,
    openUpgradeModal: () => setShowUpgradeModal(true),
    fetchTableData,
    categoryFilters,
    setCategoryFilters,
    applyCategoryFilters,
  };

  return (
    <CategoryAnalysisContext.Provider value={value}>
      {children}
      <AppsTrackingPlanModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        title=" Upgrade your plan to unlock this feature"
      />
    </CategoryAnalysisContext.Provider>
  );
};
